<script setup>

import {onMounted, ref} from "vue";
import {useNotificationStore} from '@/stores/NotificationStore.js'

const notificationStore = useNotificationStore()

const props = defineProps({
    type: String,
    version: {
        type: Number,
        required: false,
        default: 3,
    },
    draftAutomations: Object,
    completedAutomations: Object,
    activeAutomations: Object,
    smsSettingsAvailable: Boolean,
    emailSettingsAvailable: Boolean,
});

const counts = ref({
    draft: 0,
    active: 0,
    completed: 0,
})
const draftAutomations = ref([])
const completedAutomations = ref([])
const activeAutomations = ref([])
const loading = ref(false)
const perPage = ref(10)
const pagination = ref({
    completed: {current_page: 1, last_page: 1, total: 0, per_page: perPage.value},
    active: {current_page: 1, last_page: 1, total: 0, per_page: perPage.value},
    drafts: {current_page: 1, last_page: 1, total: 0, per_page: perPage.value},
})
const loadedCategories = ref(new Set(['completed'])) // Track which categories have been loaded

// Load a specific category with pagination
const loadCategory = async (category, page = 1) => {
    loading.value = true
    try {
        const res = await axios.get(route('crm.communications.apiIndex', props.type), {
            params: {
                category: category,
                per_page: perPage.value,
                page: page,
            }
        })

        if (category === 'completed') {
            // Append if loading more pages, replace if loading first page
            if (page === 1) {
                completedAutomations.value = res.data.completed_automations
            } else {
                completedAutomations.value.push(...res.data.completed_automations)
            }
            if (res.data.pagination) {
                pagination.value.completed = res.data.pagination
            }
        } else if (category === 'active') {
            if (page === 1) {
                activeAutomations.value = res.data.active_automations
            } else {
                activeAutomations.value.push(...res.data.active_automations)
            }
            if (res.data.pagination) {
                pagination.value.active = res.data.pagination
            }
        } else if (category === 'drafts') {
            if (page === 1) {
                draftAutomations.value = res.data.draft_automations
            } else {
                draftAutomations.value.push(...res.data.draft_automations)
            }
            if (res.data.pagination) {
                pagination.value.drafts = res.data.pagination
            }
        }
        loadedCategories.value.add(category)
    } catch (error) {
        notificationStore.addNotification({
            type: 'error',
            title: 'Error',
            message: 'Failed to load automations',
            dismissAfter: 5000,
        })
    } finally {
        await loadCounts()
        loading.value = false
    }
}

const loadCounts = async () => {
    const res = await axios.get(route('crm.communications.counts', props.type))
    counts.value.draft = res.data.draft_count
    counts.value.active = res.data.active_count
    counts.value.completed = res.data.completed_count
}

// Load all categories (for initial load or refresh)
const loadAutomations = async () => {
    const defaultCategory = props.type === 'newsletter' ? 'completed' : 'active'
    loading.value = true
    try {
        // Load only completed by default for performance (prevents timeout)
        const res = await axios.get(route('crm.communications.apiIndex', props.type), {
            params: {
                category: defaultCategory, // Load completed first to avoid timeout
                per_page: perPage.value,
                page: 1,
            }
        })

        // Ensure we're working with arrays
        const completed = Array.isArray(res.data.completed_automations) ? res.data.completed_automations : []
        const active = Array.isArray(res.data.active_automations) ? res.data.active_automations : []
        const drafts = Array.isArray(res.data.draft_automations) ? res.data.draft_automations : []

        completedAutomations.value = completed
        activeAutomations.value = active
        draftAutomations.value = drafts

        // Update pagination info
        if (res.data.pagination) {
            pagination.value[defaultCategory] = {
                current_page: res.data.pagination.current_page || 1,
                last_page: res.data.pagination.last_page || 1,
                per_page: res.data.pagination.per_page || perPage.value,
                total: res.data.pagination.total || 0,
            }
        }
    } catch (error) {
        notificationStore.addNotification({
            type: 'error',
            title: 'Error',
            message: 'Failed to load automations',
            dismissAfter: 5000,
        })
    } finally {
        await loadCounts()
        loading.value = false
    }
}

// Load category when tab is activated (lazy loading)
const onTabChange = (tabValue) => {
    if (tabValue === 'scheduled' && !loadedCategories.value.has('active')) {
        loadCategory('active')
    } else if (tabValue === 'drafts' && !loadedCategories.value.has('drafts')) {
        loadCategory('drafts')
    } else if (tabValue === 'history' && !loadedCategories.value.has('completed')) {
        loadCategory('completed')
    }
}

const unscheduleAutomation = async (automation) => {
    const res = await axios.post(route('crm.communications.single-automation.unschedule', automation.id))

    if (res.data.success) {
        // Reload the active category
        await loadCategory('active', pagination.value.active.current_page)
        // Also reload completed in case it moved there
        await loadCategory('completed', pagination.value.completed.current_page)

        const message = props.type === 'newsletter' ? 'communication:newsletter.unscheduled' : 'communication:automation.unscheduled'

        notificationStore.addNotification({
            type: 'success',
            message: _mft(message),
            dismissAfter: 3500,
        })
    } else {
        notificationStore.addNotification({
            type: 'error',
            title: 'Error',
            message: res.data.message,
            dismissAfter: 5000,
        })
    }
}

const deleteAutomation = async (automation) => {
    const res = await axios.post(route('crm.communications.single-automation.delete', automation.id))

    if (res.data.success) {
        // Reload the drafts category
        await loadCategory('drafts', pagination.value.drafts.current_page)

        const message = props.type === 'newsletter' ? 'communication:newsletter.deleted' : 'communication:automation.deleted'

        notificationStore.addNotification({
            type: 'success',
            message: _mft(message),
            dismissAfter: 3500,
        })
    } else {
        const errorMessage = props.type === 'newsletter' ? 'communication:newsletter.cannotBeDeleted.onlyDrafts' : 'communication:automation.cannotBeDeleted.onlyDrafts'

        notificationStore.addNotification({
            type: 'error',
            message: _mft(errorMessage),
            dismissAfter: 5000,
        })
    }
}

const getEditAutomationUrl = (id) => {
    if (props.type === 'newsletter' && props.version > 3) {
        return route('bo.crm.newsletters.edit', id)
    }
    if (props.type === 'automation' && props.version > 3) {
        return route('bo.crm.automations.edit', id)
    }
    return route('crm.communications.automations.show', id)
}

onMounted(() => {
    loadAutomations()
})

</script>
<template>
    <div class="mb-5">
        <card-tabbed
            v-if="type === 'newsletter'"
            @changestatustab="onTabChange"
        >
            <card-tabbed-tab
                :title="_mft('communication:automation.scheduled') + ' ' + (counts.active > 0 ? '(' + counts.active + ')' : '')"
                value="scheduled"
                :active="false"
            >
                <list-basic
                    list-class="bg-transparent -mx-4"
                >
                    <list-basic-item v-if="activeAutomations.length === 0">
                        <div class="text-gray-400 text-center">
                            {{ _mft('communication:noScheduledFound.newsletter') }}
                        </div>
                    </list-basic-item>

                    <list-basic-item
                        v-for="automation in activeAutomations"
                        :key="automation.id"
                    >
                        <template #status>
                            <container-with-tooltip
                                alignment="center"
                                vertical-alignment="top"
                                class="w-4 h-4 mr-2 mt-1 flex justify-center items-center"
                            >
                                <span class="relative">
                                    <i class="fa-regular fa-clock text-gray-500 text-sm"></i>
                                </span>

                                <template #tooltip>
                                    <div class="text-xs bg-white rounded-md p-1">
                                        {{ _mft('communication:automation.scheduled') }}
                                    </div>
                                </template>
                            </container-with-tooltip>
                        </template>

                        <template #title>
                            <a
                                :href="getEditAutomationUrl(automation.id)"
                                class="mf-link"
                            >
                                {{ automation.name }}
                            </a>
                        </template>

                        <div class="text-xs text-gray-400">
                            <span v-if="automation.send_rule === 'once_to_all'">{{
                                    _mft('communication:sendRuleTypes.onceToAll')
                                }}</span>
                            <span v-if="automation.send_rule === 'once_assigned_in_time_range'">{{
                                    _mft('communication:sendRuleTypes.onceAssignedInTimeRange')
                                }}</span>
                            <span class="ml-1 lowercase">{{
                                    _mft('communication:automation.scheduledAt')
                                }} {{ automation.activate_at_display }}</span>
                        </div>

                        <div
                            class="text-xs text-gray-400"
                        >
                            <ul class="flex flex-wrap mt-1">
                                <li
                                    v-for="(tag, i) in automation.included_lead_tags"
                                    :key="i"
                                    class="pr-0.5 pb-0.5"
                                >
                                    <pill
                                        size="xs"
                                        class="bg-gray-400 text-white cursor-default"
                                    >
                                        {{ tag.name }}
                                    </pill>
                                </li>

                                <li
                                    v-for="(tag, i) in automation.excluded_lead_tags"
                                    :key="i"
                                    class="pr-0.5 pb-0.5"
                                >
                                    <container-with-tooltip>
                                        <pill
                                            size="xs"
                                            class="bg-myflowRed text-white cursor-default"
                                        >
                                            {{ tag.name }}
                                        </pill>
                                        <template #tooltip>
                                            <div class="text-xxs bg-white rounded-md p-1 whitespace-nowrap text-black">
                                                {{ _mft('communication:tag.thisTagIsExcluded') }}
                                            </div>
                                        </template>
                                    </container-with-tooltip>
                                </li>
                            </ul>
                        </div>

                        <template #actions>
                            <div class="flex space-x-1">
                                <div class="flex space-x-1">
                                    <a
                                        v-if="props.version > 3 &&  automation.last_performed_at !== null"
                                        :href="route('bo.crm.automations.deliveries', automation.id)"
                                        class="mf-link"
                                    >
                                        <button-secondary size="sm">
                                            {{ _mft('communication:automation.deliveries') }}
                                        </button-secondary>
                                    </a>
                                    <a
                                        :href="getEditAutomationUrl(automation.id)"
                                        class="mf-link"
                                    >
                                        <button-secondary size="sm">
                                            <i class="fa-regular fa-cog" />
                                        </button-secondary>
                                    </a>
                                </div>
                                <button-more-options
                                    size="sm"
                                    alignment="right"
                                >
                                    <button-more-options-option-with-confirmation
                                        :confirm-button-label="_mft('shared:confirm.positive') + ',  '+ _mft('communication:newsletter.unschedule').toLowerCase()"
                                        :confirm-text="_mft('shared:confirm.areYouSure')"
                                        @confirmed="unscheduleAutomation(automation)"
                                    >
                                        <div class="min-w-24 text-center">
                                            {{ _mft('communication:newsletter.unschedule') }}
                                        </div>
                                    </button-more-options-option-with-confirmation>
                                </button-more-options>
                            </div>
                        </template>
                    </list-basic-item>
                </list-basic>
                <div
                    v-if="pagination.active.last_page > pagination.active.current_page"
                    class="mt-4 text-center"
                >
                    <button-secondary
                        size="sm"
                        @click="loadCategory('active', pagination.active.current_page + 1)"
                        :disabled="loading"
                    >
                        {{ loading ? _mft('shared:pagination.loading') : _mft('shared:pagination.loadMore') }}
                    </button-secondary>
                </div>
            </card-tabbed-tab>
            <card-tabbed-tab
                :title="_mft('communication:automation.drafts') + ' ' + (counts.draft > 0 ? '(' + counts.draft + ')' : '')"
                value="drafts"
                :active="false"
            >
                <list-basic
                    list-class="bg-transparent -mx-4"
                >
                    <list-basic-item v-if="draftAutomations.length === 0">
                        <div class="text-gray-400 text-center">
                            {{ _mft('communication:noDraftsFound.newsletter') }}
                        </div>
                    </list-basic-item>

                    <list-basic-item
                        v-for="automation in draftAutomations"
                        :key="automation.id"
                    >
                        <template #title>
                            <a
                                :href="getEditAutomationUrl(automation.id)"
                                class="mf-link"
                            >
                                {{ automation.name }}
                            </a>
                        </template>

                        <div
                            class="text-xs text-gray-400"
                        >
                            <span v-if="automation.send_rule === 'once_to_all'">{{
                                    _mft('communication:sendRuleTypes.onceToAll')
                                }}</span>
                            <span v-if="automation.send_rule === 'once_assigned_in_time_range'">{{
                                    _mft('communication:sendRuleTypes.onceAssignedInTimeRange')
                                }}</span>
                            <ul class="flex flex-wrap mt-1">
                                <li
                                    v-for="(tag, i) in automation.included_lead_tags"
                                    :key="i"
                                    class="pr-0.5 pb-0.5"
                                >
                                    <pill
                                        size="xs"
                                        class="bg-gray-400 text-white cursor-default"
                                    >
                                        {{ tag.name }}
                                    </pill>
                                </li>

                                <li
                                    v-for="(tag, i) in automation.excluded_lead_tags"
                                    :key="i"
                                    class="pr-0.5 pb-0.5"
                                >
                                    <container-with-tooltip>
                                        <pill
                                            size="xs"
                                            class="bg-myflowRed text-white cursor-default"
                                        >
                                            {{ tag.name }}
                                        </pill>
                                        <template #tooltip>
                                            <div class="text-xxs bg-white rounded-md p-1 whitespace-nowrap text-black">
                                                {{ _mft('communication:tag.thisTagIsExcluded') }}
                                            </div>
                                        </template>
                                    </container-with-tooltip>
                                </li>
                            </ul>
                        </div>

                        <template #status>
                            <container-with-tooltip
                                alignment="center"
                                vertical-alignment="top"
                                class="w-4 h-4 mr-2 mt-1 flex justify-center items-center"
                            >
                                <span class="relative">
                                    <i class="fa-regular fa-file-pen text-gray-400 text-sm"></i>
                                </span>

                                <template #tooltip>
                                    <div class="text-xs bg-white rounded-md p-1">
                                        {{ _mft('communication:automation.draft') }}
                                    </div>
                                </template>
                            </container-with-tooltip>
                        </template>

                        <template #actions>
                            <div class="flex space-x-1">
                                <button-more-options
                                    size="sm"
                                    alignment="right"
                                >
                                    <button-more-options-option-with-confirmation
                                        :confirm-button-label="_mft('shared:action.delete')"
                                        :confirm-text="_mft('shared:confirm.areYouSure')"
                                        @confirmed="deleteAutomation(automation)"
                                    >
                                        <div class="min-w-24 text-center">{{ _mft('shared:action.delete') }}</div>
                                    </button-more-options-option-with-confirmation>
                                </button-more-options>
                                <div class="flex space-x-1">
                                    <a
                                        :href="getEditAutomationUrl(automation.id)"
                                        class="mf-link"
                                    >
                                        <button-secondary size="sm">
                                            <i class="fa-regular fa-pencil"></i>
                                        </button-secondary>
                                    </a>
                                </div>
                            </div>
                        </template>
                    </list-basic-item>
                </list-basic>
                <div
                    v-if="pagination.drafts.last_page > pagination.drafts.current_page"
                    class="mt-4 text-center"
                >
                    <button-secondary
                        size="sm"
                        @click="loadCategory('drafts', pagination.drafts.current_page + 1)"
                        :disabled="loading"
                    >
                        {{ loading ? _mft('shared:pagination.loading') : _mft('shared:pagination.loadMore') }}
                    </button-secondary>
                </div>
            </card-tabbed-tab>
            <card-tabbed-tab
                :title="_mft('communication:automation.sent') + ' ' + (counts.completed > 0 ? '(' + counts.completed + ')' : '')"
                value="history"
                :active="true"
            >
                <list-basic
                    list-class="bg-transparent -mx-4"
                >
                    <list-basic-item v-if="completedAutomations.length === 0 && !loading">
                        <div class="text-gray-400 text-center">
                            {{ _mft('communication:noCompletedFound.newsletter') }}
                        </div>
                    </list-basic-item>

                    <list-basic-item
                        v-for="(automation, i) in completedAutomations"
                        :key="automation.id"
                    >
                        <template #status>
                            <container-with-tooltip
                                alignment="center"
                                vertical-alignment="top"
                                class="w-4 h-4 mr-2 mt-1 flex justify-center items-center"
                            >
                                <span class="relative">
                                    <i class="fa-regular fa-circle-check text-myflowGreen text-sm"></i>
                                    <i
                                        v-if="automation.stats.failed_deliveries > 0"
                                        class="fa-solid fa-triangle-exclamation text-xs absolute -top-1 -right-1"
                                        :class="automation.stats.labels.includes('high_failure_rate') ? 'text-myflowRed' : 'text-myflowYellow'"
                                    ></i>
                                </span>

                                <template #tooltip>
                                    <div class="text-xs bg-white rounded-md p-1">
                                        {{
                                            automation.stats.failed_deliveries === 0 ? _mft('communication:automation.completed') : _mft('communication:automation.completedWithIssues')
                                        }}
                                    </div>
                                </template>
                            </container-with-tooltip>
                        </template>

                        <template #title>
                            <a
                                :href="getEditAutomationUrl(automation.id)"
                                class="mf-link"
                            >
                                {{ automation.name }}
                            </a>
                        </template>

                        <div class="text-xs text-gray-400">
                            <span v-if="automation.send_rule === 'once_to_all'">{{
                                    _mft('communication:sendRuleTypes.onceToAll')
                                }}</span>
                            <span v-if="automation.send_rule === 'once_assigned_in_time_range'">{{
                                    _mft('communication:sendRuleTypes.onceAssignedInTimeRange')
                                }}</span>
                            <span class="ml-1 lowercase">{{
                                    _mft('communication:automation.scheduledAt')
                                }} {{ automation.activate_at_display }}</span>
                        </div>

                        <div
                            class="text-xs text-gray-400"
                        >
                            <ul class="flex flex-wrap mt-1">
                                <li
                                    v-for="(tag, i) in automation.included_lead_tags"
                                    :key="i"
                                    class="pr-0.5 pb-0.5"
                                >
                                    <pill
                                        size="xs"
                                        class="bg-gray-400 text-white cursor-default"
                                    >
                                        {{ tag.name }}
                                    </pill>
                                </li>

                                <li
                                    v-for="(tag, i) in automation.excluded_lead_tags"
                                    :key="i"
                                    class="pr-0.5 pb-0.5"
                                >
                                    <container-with-tooltip>
                                        <pill
                                            size="xs"
                                            class="bg-myflowRed text-white cursor-default"
                                        >
                                            {{ tag.name }}
                                        </pill>
                                        <template #tooltip>
                                            <div class="text-xxs bg-white rounded-md p-1 whitespace-nowrap text-black">
                                                {{ _mft('communication:tag.thisTagIsExcluded') }}
                                            </div>
                                        </template>
                                    </container-with-tooltip>
                                </li>
                            </ul>
                        </div>

                        <communications-stats
                            class="flex space-x-4"
                            :stats="automation.stats"
                        />

                        <template #actions>
                            <div class="flex flex-col space-y-1 items-end">
                                <a
                                    :href="getEditAutomationUrl(automation.id)"
                                    class="mf-link"
                                >
                                    <button-base size="sm">
                                        {{ _mft('communication:automation.show') }}
                                    </button-base>
                                </a>
                                <button-more-options
                                    size="sm"
                                    alignment="right"
                                >
                                    <button-more-options-option disabled>
                                        <div class="min-w-24 text-center text-gray-400 italic text-xs">
                                            {{ _mft('communication:newsletter.cannotBeDeleted.onlyDrafts') }}
                                        </div>
                                    </button-more-options-option>
                                </button-more-options>
                            </div>
                        </template>
                    </list-basic-item>
                </list-basic>
                <div
                    v-if="pagination.completed.last_page > pagination.completed.current_page"
                    class="mt-4 text-center"
                >
                    <button-secondary
                        size="sm"
                        @click="loadCategory('completed', pagination.completed.current_page + 1)"
                        :disabled="loading"
                    >
                        {{ loading ? _mft('shared:pagination.loading') : _mft('shared:pagination.loadMore') }}
                    </button-secondary>
                </div>
            </card-tabbed-tab>
        </card-tabbed>


        <card-tabbed
            v-if="type === 'automation'"
            @changestatustab="onTabChange"
        >
            <card-tabbed-tab
                :title="_mft('communication:automation.active') + ' ' + (counts.active > 0 ? '(' + counts.active + ')' : '')"
                value="scheduled"
                :active="true"
            >
                <list-basic
                    list-class="bg-transparent -mx-4"
                >
                    <list-basic-item v-if="activeAutomations.length === 0">
                        <div class="text-gray-400 text-center">
                            {{ _mft('communication:noActiveFound.automation') }}
                        </div>
                    </list-basic-item>

                    <list-basic-item
                        v-for="automation in activeAutomations"
                        :key="automation.id"
                    >
                        <template #status>
                            <container-with-tooltip
                                alignment="center"
                                vertical-alignment="top"
                                class="w-4 h-4 mr-2 mt-1 flex justify-center items-center"
                            >
                                <span class="relative">
                                    <i class="fa-regular fa-play text-myflowGreen text-sm"></i>
                                </span>

                                <template #tooltip>
                                    <div class="text-xs bg-white rounded-md p-1">
                                        {{ _mft('communication:automation.active') }}
                                    </div>
                                </template>
                            </container-with-tooltip>
                        </template>

                        <template #title>
                            <a
                                :href="getEditAutomationUrl(automation.id)"
                                class="mf-link"
                            >
                                {{ automation.name }}
                            </a>
                        </template>

                        <div class="text-xs text-gray-400">
                            {{ _mft('communication:composer.summary.automation.activeFrom') }}
                            {{ automation.activate_at_display }}
                        </div>

                        <div
                            class="text-xs text-gray-400"
                        >
                            <ul class="flex flex-wrap mt-1">
                                <li
                                    v-for="(tag, i) in automation.included_lead_tags"
                                    :key="i"
                                    class="pr-0.5 pb-0.5"
                                >
                                    <pill
                                        size="xs"
                                        class="bg-gray-400 text-white cursor-default"
                                    >
                                        {{ tag.name }}
                                    </pill>
                                </li>

                                <li
                                    v-for="(tag, i) in automation.excluded_lead_tags"
                                    :key="i"
                                    class="pr-0.5 pb-0.5"
                                >
                                    <container-with-tooltip>
                                        <pill
                                            size="xs"
                                            class="bg-myflowRed text-white cursor-default"
                                        >
                                            {{ tag.name }}
                                        </pill>
                                        <template #tooltip>
                                            <div class="text-xxs bg-white rounded-md p-1 whitespace-nowrap text-black">
                                                {{ _mft('communication:tag.thisTagIsExcluded') }}
                                            </div>
                                        </template>
                                    </container-with-tooltip>
                                </li>
                            </ul>
                        </div>

                        <communications-stats
                            class="flex space-x-4"
                            :stats="automation.stats"
                        />

                        <template #actions>
                            <div class="flex space-x-1">
                                <div class="flex space-x-1">
                                    <a
                                        v-if="props.version > 3 &&  automation.last_performed_at !== null"
                                        :href="route('bo.crm.automations.deliveries', automation.id)"
                                        class="mf-link"
                                    >
                                        <button-base size="sm">
                                            {{ _mft('communication:automation.deliveries') }}
                                        </button-base>
                                    </a>
                                    <a
                                        :href="getEditAutomationUrl(automation.id)"
                                        class="mf-link"
                                    >
                                        <button-secondary size="sm">
                                            <i class="fa-regular fa-cog" />
                                        </button-secondary>
                                    </a>

                                </div>
                                <button-more-options
                                    size="sm"
                                    alignment="right"
                                >
                                    <button-more-options-option-with-confirmation
                                        :confirm-button-label="_mft('shared:confirm.positive') + ',  '+ _mft('communication:automation.unschedule').toLowerCase()"
                                        :confirm-text="_mft('shared:confirm.areYouSure')"
                                        @confirmed="unscheduleAutomation(automation)"
                                    >
                                        <div class="min-w-24 text-center">
                                            {{ _mft('communication:automation.unschedule') }}
                                        </div>
                                    </button-more-options-option-with-confirmation>
                                </button-more-options>
                            </div>
                        </template>
                    </list-basic-item>
                </list-basic>
                <div
                    v-if="pagination.active.last_page > pagination.active.current_page"
                    class="mt-4 text-center"
                >
                    <button-secondary
                        size="sm"
                        @click="loadCategory('active', pagination.active.current_page + 1)"
                        :disabled="loading"
                    >
                        {{ loading ? _mft('shared:pagination.loading') : _mft('shared:pagination.loadMore') }}
                    </button-secondary>
                </div>
            </card-tabbed-tab>
            <card-tabbed-tab
                :title="_mft('communication:automation.drafts') + ' ' + (counts.draft > 0 ? '(' + counts.draft + ')' : '')"
                value="drafts"
                :active="false"
            >
                <list-basic
                    list-class="bg-transparent -mx-4"
                >
                    <list-basic-item v-if="draftAutomations.length === 0">
                        <div class="text-gray-400 text-center">
                            {{ _mft('communication:noDraftsFound.automation') }}
                        </div>
                    </list-basic-item>

                    <list-basic-item
                        v-for="automation in draftAutomations"
                        :key="automation.id"
                    >
                        <template #title>
                            <a
                                :href="getEditAutomationUrl(automation.id)"
                                class="mf-link"
                            >
                                {{ automation.name }}
                            </a>
                        </template>

                        <div
                            class="text-xs text-gray-400"
                        >
                            <ul class="flex flex-wrap mt-1">
                                <li
                                    v-for="(tag, i) in automation.included_lead_tags"
                                    :key="i"
                                    class="pr-0.5 pb-0.5"
                                >
                                    <pill
                                        size="xs"
                                        class="bg-gray-400 text-white cursor-default"
                                    >
                                        {{ tag.name }}
                                    </pill>
                                </li>

                                <li
                                    v-for="(tag, i) in automation.excluded_lead_tags"
                                    :key="i"
                                    class="pr-0.5 pb-0.5"
                                >
                                    <container-with-tooltip>
                                        <pill
                                            size="xs"
                                            class="bg-myflowRed text-white cursor-default"
                                        >
                                            {{ tag.name }}
                                        </pill>
                                        <template #tooltip>
                                            <div class="text-xxs bg-white rounded-md p-1 whitespace-nowrap text-black">
                                                {{ _mft('communication:tag.thisTagIsExcluded') }}
                                            </div>
                                        </template>
                                    </container-with-tooltip>
                                </li>
                            </ul>
                        </div>

                        <template #status>
                            <container-with-tooltip
                                alignment="center"
                                vertical-alignment="top"
                                class="w-4 h-4 mr-2 mt-1 flex justify-center items-center"
                            >
                                <span class="relative">
                                    <i class="fa-regular fa-pencil text-gray-400 text-sm"></i>
                                </span>

                                <template #tooltip>
                                    <div class="text-xs bg-white rounded-md p-1">
                                        {{ _mft('communication:automation.draft') }}
                                    </div>
                                </template>
                            </container-with-tooltip>
                        </template>

                        <communications-stats
                            class="flex space-x-4"
                            :stats="automation.stats"
                        />

                        <template #actions>
                            <div class="flex flex-col space-y-1 items-end">
                                <div class="flex space-x-1">
                                    <a
                                        v-if="props.version > 3 && automation.last_performed_at !== null"
                                        :href="route('bo.crm.automations.deliveries', automation.id)"
                                        class="mf-link"
                                    >
                                        <button-base size="sm">
                                            {{ _mft('communication:automation.deliveries') }}
                                        </button-base>
                                    </a>
                                    <a
                                        :href="getEditAutomationUrl(automation.id)"
                                        class="mf-link"
                                    >
                                        <button-base size="sm">
                                            {{ _mft('shared:edit') }}
                                        </button-base>
                                    </a>
                                </div>
                                <button-more-options
                                    size="sm"
                                    alignment="right"
                                >
                                    <button-more-options-option-with-confirmation
                                        :confirm-button-label="_mft('shared:action.delete')"
                                        :confirm-text="_mft('shared:confirm.areYouSure')"
                                        @confirmed="deleteAutomation(automation)"
                                    >
                                        <div class="min-w-24 text-center">{{ _mft('shared:action.delete') }}</div>
                                    </button-more-options-option-with-confirmation>
                                </button-more-options>
                            </div>
                        </template>
                    </list-basic-item>
                </list-basic>
                <div
                    v-if="pagination.drafts.last_page > pagination.drafts.current_page"
                    class="mt-4 text-center"
                >
                    <button-secondary
                        size="sm"
                        @click="loadCategory('drafts', pagination.drafts.current_page + 1)"
                        :disabled="loading"
                    >
                        {{ loading ? _mft('shared:pagination.loading') : _mft('shared:pagination.loadMore') }}
                    </button-secondary>
                </div>
            </card-tabbed-tab>
        </card-tabbed>
    </div>
</template>
